/**
 * 附件独立演示页（T09）。
 *
 * 在 390 x 844 画布里演示完整链路：选择 → 预览（预览条）→ 移除 → 渲染成消息附件。
 * 路由 `#/attachments`，由 `src/app/router.tsx` 接线；不占用底部导航，也不改 T06 的对话页。
 *
 * 说明：本页所有可见部件都通过 `useComponentMeta` 登记进 `src/ui/registry.tsx`；
 * 布局用 390x844 画布坐标声明，渲染时由 `contentBoxStyle` / `offsetBoxStyle` 换算。
 */

import { motion } from 'framer-motion';
import { useCallback, useState } from 'react';
import type { CSSProperties, ReactElement, ReactNode } from 'react';

import type { Attachment } from '../../core/types';
import { useMotionPresets } from '../../design/motion';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import type { BoxLayout, ComponentMetaInput } from '../../ui';
import { AttachmentList } from './AttachmentList';
import { AttachmentStrip } from './AttachmentStrip';
import styles from './attachments.module.css';
import { contentBoxStyle, offsetBoxStyle } from './layout';
import { DATA_URL_WARN_MESSAGE, persistMessageWithAttachments } from './persist';
import { pickAttachments } from './select';
import type { PickResult } from './select';
import { MAX_ATTACHMENT_COUNT, hasOversizedDataUrl } from './validate';

/** 本页在组件清单里的 page 标识。*/
const PAGE_ID = 'attachments';
/** 演示用会话 id（仅用于把附件经 messageRepo 写回一条消息）。*/
const DEMO_CONVERSATION_ID = 'attachments-demo';

const PAGE: BoxLayout = { x: 0, y: 24, width: 390, height: 744 };
const TITLE: BoxLayout = { x: 16, y: 32, width: 358, height: 28 };
const HINT: BoxLayout = { x: 16, y: 62, width: 358, height: 34 };
const PICK_BUTTON: BoxLayout = { x: 16, y: 104, width: 180, height: 48 };
const CLEAR_BUTTON: BoxLayout = { x: 204, y: 104, width: 170, height: 48 };
const NOTICE: BoxLayout = { x: 16, y: 160, width: 358, height: 56 };
const STRIP_LABEL: BoxLayout = { x: 16, y: 224, width: 358, height: 20 };
const STRIP: BoxLayout = { x: 16, y: 248, width: 358, height: 116 };
const MESSAGE_LABEL: BoxLayout = { x: 16, y: 372, width: 358, height: 20 };
const MESSAGE_CARD: BoxLayout = { x: 16, y: 396, width: 358, height: 280 };
const MESSAGE_TEXT: BoxLayout = { x: 32, y: 404, width: 326, height: 22 };
const MESSAGE_LIST: BoxLayout = { x: 32, y: 434, width: 326, height: 228 };
const SEND_BUTTON: BoxLayout = { x: 16, y: 686, width: 358, height: 48 };

const PAGE_META: ComponentMetaInput = {
  id: `page.${PAGE_ID}.root`,
  page: PAGE_ID,
  role: 'container',
  label: '附件演示页容器',
  layout: PAGE,
  zIndex: 2,
  notes: '页面根容器（含顶部 24dp 状态栏安全区偏移）；安卓用 Fragment 根 View',
};

interface PageTitleProps {
  meta: ComponentMetaInput;
  children: ReactNode;
}

function PageTitle({ meta, children }: PageTitleProps): ReactElement {
  const ref = useComponentMeta<HTMLHeadingElement>(buildComponentMeta(meta, 'text'));
  return (
    <h1
      ref={ref}
      className={styles.title}
      data-ui-id={meta.id}
      data-ui-role="text"
      style={contentBoxStyle(meta.layout)}
    >
      {children}
    </h1>
  );
}

interface PageTextProps {
  meta: ComponentMetaInput;
  className?: string;
  /** 父容器（画布坐标）；传入后按父容器换算 DOM 相对位置。*/
  parent?: BoxLayout;
  children: ReactNode;
}

function PageText({ meta, className, parent, children }: PageTextProps): ReactElement {
  const ref = useComponentMeta<HTMLParagraphElement>(buildComponentMeta(meta, 'text'));
  return (
    <p
      ref={ref}
      className={className}
      data-ui-id={meta.id}
      data-ui-role="text"
      style={parent === undefined ? contentBoxStyle(meta.layout) : offsetBoxStyle(meta.layout, parent)}
    >
      {children}
    </p>
  );
}

interface PageBoxProps {
  meta: ComponentMetaInput;
  className?: string;
  children?: ReactNode;
}

function PageBox({ meta, className, children }: PageBoxProps): ReactElement {
  const ref = useComponentMeta<HTMLDivElement>(buildComponentMeta(meta, 'container'));
  return (
    <div
      ref={ref}
      className={className}
      data-ui-id={meta.id}
      data-ui-role={meta.role ?? 'container'}
      style={contentBoxStyle(meta.layout)}
    >
      {children}
    </div>
  );
}

interface PageButtonProps {
  meta: ComponentMetaInput;
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}

function PageButton({
  meta,
  variant = 'primary',
  disabled = false,
  onClick,
  children,
}: PageButtonProps): ReactElement {
  const presets = useMotionPresets();
  const ref = useComponentMeta<HTMLButtonElement>(buildComponentMeta(meta, 'button'));
  const className =
    variant === 'secondary' ? `${styles.button} ${styles.buttonSecondary}` : styles.button;
  const style: CSSProperties = contentBoxStyle(meta.layout);

  return (
    <motion.button
      ref={ref}
      type="button"
      className={className}
      data-ui-id={meta.id}
      data-ui-role="button"
      aria-label={meta.label}
      disabled={disabled}
      style={style}
      whileTap={presets.tap.whileTap}
      transition={presets.tap.transition}
      onClick={onClick}
    >
      {children}
    </motion.button>
  );
}

export interface AttachmentsPageProps {
  /** 测试注入：替换真实的系统文件选择器。*/
  picker?: (options: { existingCount: number }) => Promise<PickResult>;
}

/** 附件独立演示页。*/
export function AttachmentsPage({
  picker = pickAttachments,
}: AttachmentsPageProps = {}): ReactElement {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [rejected, setRejected] = useState<string[]>([]);
  const [messageAttachments, setMessageAttachments] = useState<Attachment[]>([]);
  const [warning, setWarning] = useState('');
  const pageRef = useComponentMeta<HTMLElement>(buildComponentMeta(PAGE_META, 'container'));

  const handlePick = useCallback((): void => {
    void picker({ existingCount: attachments.length })
      .then((result) => {
        if (result.canceled) return;
        setAttachments((prev) => [...prev, ...result.attachments]);
        setRejected(result.rejected.map((item) => item.reason));
      })
      .catch(() => {
        setRejected(['选择文件失败，请重试']);
      });
  }, [attachments.length, picker]);

  const handleRemove = useCallback((id: string): void => {
    setAttachments((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleClear = useCallback((): void => {
    setAttachments([]);
    setRejected([]);
    setWarning('');
  }, []);

  const handleSend = useCallback((): void => {
    const snapshot = attachments;
    setMessageAttachments(snapshot);
    setWarning(hasOversizedDataUrl(snapshot) ? DATA_URL_WARN_MESSAGE : '');
    void persistMessageWithAttachments(DEMO_CONVERSATION_ID, snapshot);
  }, [attachments]);

  const noticeLines: { text: string; error: boolean }[] = [];
  if (attachments.length > 0) {
    noticeLines.push({ text: `已选 ${attachments.length} 个附件`, error: false });
  }
  for (const reason of rejected) noticeLines.push({ text: reason, error: true });
  if (warning !== '') noticeLines.push({ text: warning, error: true });
  if (noticeLines.length === 0) {
    noticeLines.push({
      text: `尚未选择附件（最多 ${MAX_ATTACHMENT_COUNT} 个；图片 ≤10MB，其它 ≤20MB）`,
      error: false,
    });
  }

  return (
    <section
      ref={pageRef}
      className={styles.page}
      data-ui-id={PAGE_META.id}
      data-ui-role="container"
      data-skin-surface="page"
      style={contentBoxStyle(PAGE)}
    >
      <PageTitle meta={{ ...PAGE_META, id: `page.${PAGE_ID}.title`, label: '页面标题', layout: TITLE }}>
        附件（T09 演示）
      </PageTitle>

      <PageText
        meta={{ ...PAGE_META, id: `page.${PAGE_ID}.hint`, label: '页面说明', layout: HINT }}
        className={styles.hint}
      >
        选择图片 / 文件 → 预览 → 移除 → 渲染成消息附件。演示路由 #/attachments。
      </PageText>

      <PageButton
        meta={{
          ...PAGE_META,
          id: `page.${PAGE_ID}.pickButton`,
          role: 'button',
          label: '选择图片 / 文件',
          layout: PICK_BUTTON,
          zIndex: 5,
          notes: '隐藏 input[type=file] 触发系统选择器；安卓用 ActivityResult 文件选择',
        }}
        onClick={handlePick}
      >
        选择图片/文件
      </PageButton>

      <PageButton
        meta={{
          ...PAGE_META,
          id: `page.${PAGE_ID}.clearButton`,
          role: 'button',
          label: '清空已选附件',
          layout: CLEAR_BUTTON,
          zIndex: 5,
          props: ['disabled'],
          notes: '清空预览条；无附件时禁用',
        }}
        variant="secondary"
        disabled={attachments.length === 0}
        onClick={handleClear}
      >
        清空
      </PageButton>

      <PageBox
        meta={{
          ...PAGE_META,
          id: `page.${PAGE_ID}.notice`,
          role: 'text',
          label: '状态与拒绝原因提示',
          layout: NOTICE,
          zIndex: 4,
          notes: '展示已选数量、超限 / 类型拒绝原因与体积警告（动态文案）',
        }}
        className={styles.notice}
      >
        {noticeLines.map((line, index) => (
          <span
            key={`${String(index)}-${line.text}`}
            className={line.error ? `${styles.noticeLine} ${styles.noticeError}` : styles.noticeLine}
          >
            {line.text}
          </span>
        ))}
      </PageBox>

      <PageText
        meta={{ ...PAGE_META, id: `page.${PAGE_ID}.stripLabel`, label: '预览条标题', layout: STRIP_LABEL }}
        className={styles.hint}
      >
        已选附件（预览条）
      </PageText>

      <AttachmentStrip
        attachments={attachments}
        layout={STRIP}
        page={PAGE_ID}
        onRemove={handleRemove}
      />

      <PageText
        meta={{
          ...PAGE_META,
          id: `page.${PAGE_ID}.messageLabel`,
          label: '消息附件标题',
          layout: MESSAGE_LABEL,
        }}
        className={styles.hint}
      >
        消息内附件（AttachmentList）
      </PageText>

      <PageBox
        meta={{
          ...PAGE_META,
          id: `page.${PAGE_ID}.messageCard`,
          role: 'container',
          label: '示例消息气泡',
          layout: MESSAGE_CARD,
          zIndex: 3,
          notes: '模拟消息气泡；安卓用 RecyclerView item 气泡容器',
        }}
        className={styles.messageCard}
      >
        <PageText
          meta={{
            ...PAGE_META,
            id: `page.${PAGE_ID}.messageText`,
            label: '示例消息正文',
            layout: MESSAGE_TEXT,
          }}
          className={styles.messageText}
          parent={MESSAGE_CARD}
        >
          示例消息（附件已渲染）
        </PageText>
      </PageBox>

      <AttachmentList attachments={messageAttachments} layout={MESSAGE_LIST} page={PAGE_ID} />

      <PageButton
        meta={{
          ...PAGE_META,
          id: `page.${PAGE_ID}.sendButton`,
          role: 'button',
          label: '把附件存入消息',
          layout: SEND_BUTTON,
          zIndex: 5,
          props: ['disabled'],
          notes: '经 messageRepo.updateMessage 写回消息并渲染；无附件时禁用',
        }}
        disabled={attachments.length === 0}
        onClick={handleSend}
      >
        存入消息并渲染
      </PageButton>
    </section>
  );
}
